import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { logger } from '@/utils/logger';
import { isStaleAssetError, reloadForNewVersion } from '@/utils/staleAppReload';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Arquivo antigo depois de um Publish: recarregar resolve, sem o cliente perceber.
    if (isStaleAssetError(error) && reloadForNewVersion()) return;

    logger.error('ErrorBoundary caught an error', error, {
      component: 'ErrorBoundary',
      metadata: { errorInfo: errorInfo.componentStack },
    });

    this.setState({
      error,
      errorInfo,
    });

    this.props.onError?.(error, errorInfo);
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <Card className="w-full max-w-lg">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <AlertCircle className="h-12 w-12 text-destructive" />
              </div>
              <CardTitle className="text-xl">Algo não saiu como esperado</CardTitle>
              <CardDescription>
                Tente novamente — seu pedido está guardado. Se continuar, fale com o suporte.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {import.meta.env.DEV && this.state.error && (
                <div className="p-4 bg-muted rounded-md">
                  <p className="text-sm font-medium mb-2">Detalhes do erro (desenvolvimento):</p>
                  <pre className="text-xs overflow-auto whitespace-pre-wrap">
                    {this.state.error.message}
                  </pre>
                </div>
              )}
              {!import.meta.env.DEV && this.state.error && (
                // Código curto para o cliente enviar ao suporte (print): mostra qual parte da tela falhou.
                <p className="text-[11px] leading-snug text-muted-foreground break-words">
                  Código do erro: {String(this.state.error.message || this.state.error.name || 'desconhecido').slice(0, 140)}
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  onClick={this.handleReset}
                  className="flex-1"
                  variant="default"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Tentar Novamente
                </Button>
                <Button
                  onClick={() => window.location.reload()}
                  variant="outline"
                  className="flex-1"
                >
                  Recarregar Página
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}